import { v4 as uuidV4 } from 'uuid';
import {
  HubOption,
  StateCondition,
  SubCondition,
  HubOptionStateValue,
  ConditionQuantifier,
  HubConditionOperator,
} from 'dorian-shared/types/branch/HubOption';
import type {
  ReplayCondition, TextDestCondition, IconCondition, TagCondition, HubSettings,
  Mod,
} from 'dorian-shared/types/branch/HubOption';

/**
 * Convert a SubCondition's value fields to the v3 mods rule value format.
 * intValue (literal string) takes priority; varValue is a variable reference.
 */
function toRuleValue(sub: { intValue?: string; varValue?: number }): string | { type: 'variable'; variableId: number } {
  if (sub.varValue && sub.varValue > 0) {
    return { type: 'variable', variableId: sub.varValue };
  }
  return sub.intValue ?? '';
}

/**
 * Convert a StateCondition to a v3 mod with prop: "state".
 */
function stateConditionToMod(condition: StateCondition, sortOrder: number): Mod {
  const val: Record<string, unknown> = { state: condition.resultState };
  if (condition.resultState === 'disabled' && condition.disabledText) {
    val.text = condition.disabledText;
  }

  return {
    prop: 'state',
    sortOrder,
    val,
    when: {
      match: condition.quantifier,
      rules: condition.subConditions.map((sub) => ({
        variableId: sub.variableId,
        operator: sub.operator,
        value: toRuleValue(sub),
      })),
    },
  };
}

/**
 * Convert a mod rule value back to SubCondition intValue/varValue fields.
 */
function fromRuleValue(value: string | { type: 'variable'; variableId: number }): Pick<SubCondition, 'intValue' | 'varValue'> {
  if (typeof value === 'object' && value.type === 'variable') {
    return { varValue: value.variableId };
  }
  return { intValue: String(value) };
}

/**
 * Deserialize mods with prop: "state" into StateCondition[] for frontend UI.
 * Called on GET response to populate stateConditions from backend mods array.
 * Generates fresh UUIDs for React keys and DnD (backend doesn't store UUIDs for conditions).
 */
export function deserializeStateConditions(mods: unknown[]): StateCondition[] {
  const typedMods = (mods ?? []) as Mod[];
  const stateMods = typedMods
    .filter((m) => m.prop === 'state')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return stateMods.map((mod): StateCondition => ({
    uuid: uuidV4(),
    quantifier: (mod.when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All),
    resultState: (mod.val.state as HubOptionStateValue) ?? HubOptionStateValue.Hidden,
    disabledText: mod.val.state === 'disabled' ? (mod.val.text as string | undefined) : undefined,
    subConditions: mod.when.rules.map((rule): SubCondition => ({
      uuid: uuidV4(),
      variableId: rule.variableId,
      operator: rule.operator as HubConditionOperator,
      ...fromRuleValue(rule.value),
    })),
  }));
}

/**
 * Build the `when` clause from a condition's sub-conditions.
 */
function buildWhenClause(condition: { quantifier: string; subConditions: SubCondition[] }): Mod['when'] {
  return {
    match: condition.quantifier,
    rules: condition.subConditions.map((sub) => ({
      variableId: sub.variableId,
      operator: sub.operator,
      value: toRuleValue(sub),
    })),
  };
}

/**
 * Convert one ReplayCondition to up to 4 mods (same `when` clause).
 * Null result fields are skipped.
 */
function replayConditionToMods(condition: ReplayCondition, sortOrder: number): Mod[] {
  const when = buildWhenClause(condition);
  const mods: Mod[] = [];

  if (condition.resultState != null) {
    mods.push({
      prop: 'replay.state', sortOrder, val: { state: condition.resultState }, when,
    });
  }

  if (condition.resultIconId != null) {
    // replay.icon pending v3 spec update — architect confirmed 2026-03-17
    mods.push({
      prop: 'replay.icon', sortOrder, val: { iconId: condition.resultIconId }, when,
    });
  }

  if (condition.resultText != null) {
    // Empty string "" is intentional override; null means "not set"
    mods.push({
      prop: 'replay.text', sortOrder, val: { text: condition.resultText }, when,
    });
  }

  if (condition.resultGotoBranchId != null) {
    mods.push({
      prop: 'replay.goto', sortOrder, val: { gotoBranchId: condition.resultGotoBranchId }, when,
    });
  }

  return mods;
}

/**
 * Convert one TextDestCondition to up to 2 mods (title + goto, same `when` clause).
 * Null result fields are skipped.
 */
function textDestConditionToMods(condition: TextDestCondition, sortOrder: number): Mod[] {
  const when = buildWhenClause(condition);
  const mods: Mod[] = [];

  if (condition.resultText != null) {
    mods.push({
      prop: 'title', sortOrder, val: { text: condition.resultText }, when,
    });
  }

  // Always emit a goto mod (even with null gotoBranchId) so the override row survives
  // a save/reload round-trip while the author is still filling it in. Backend save accepts
  // null; publish-time validation still flags missing destinations.
  mods.push({
    prop: 'goto', sortOrder, val: { gotoBranchId: condition.resultGotoBranchId }, when,
  });

  return mods;
}

/**
 * Deserialize mods with prop: "title" and "goto" into TextDestCondition[] for frontend UI.
 * Groups title and goto mods by sortOrder — same sortOrder = same condition card.
 */
export function deserializeTextDestConditions(mods: unknown[]): TextDestCondition[] {
  const typedMods = (mods ?? []) as Mod[];
  const titleMods = typedMods.filter((m) => m.prop === 'title').sort((a, b) => a.sortOrder - b.sortOrder);
  const gotoMods = typedMods.filter((m) => m.prop === 'goto').sort((a, b) => a.sortOrder - b.sortOrder);

  // Merge by sortOrder: title and goto with same sortOrder belong to the same condition
  const sortOrders = new Set([
    ...titleMods.map((m) => m.sortOrder),
    ...gotoMods.map((m) => m.sortOrder),
  ]);

  return [...sortOrders].sort((a, b) => a - b).map((so): TextDestCondition => {
    const titleMod = titleMods.find((m) => m.sortOrder === so);
    const gotoMod = gotoMods.find((m) => m.sortOrder === so);
    // Use whichever mod exists to reconstruct the when clause
    const sourceMod = titleMod ?? gotoMod!;

    return {
      uuid: uuidV4(),
      quantifier: sourceMod.when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All,
      subConditions: sourceMod.when.rules.map((rule): SubCondition => ({
        uuid: uuidV4(),
        variableId: rule.variableId,
        operator: rule.operator as HubConditionOperator,
        ...fromRuleValue(rule.value),
      })),
      resultText: titleMod ? (titleMod.val.text as string) : null,
      resultGotoBranchId: gotoMod ? (gotoMod.val.gotoBranchId as number | null) : null,
    };
  });
}

/**
 * Mods serializer registry. Feature branches register their serializers here.
 * Each serializer returns { mods: serialized mods, handledProps: props it owns }.
 */
type ModsSerializerResult = { mods: unknown[]; handledProps: string[] };
type ModsSerializer = (option: HubOption) => ModsSerializerResult;

const modsSerializers: ModsSerializer[] = [];

/** Register a mods serializer. Called once at module load by feature modules. Idempotent under HMR. */
export function registerModsSerializer(serializer: ModsSerializer): void {
  if (!modsSerializers.includes(serializer)) {
    modsSerializers.push(serializer);
  }
}

// Built-in serializers: state + replay (V1.0 + V1.1)
registerModsSerializer((option) => {
  const stateConditions = option.stateConditions ?? [];
  return {
    mods: stateConditions.map((cond, i) => stateConditionToMod(cond, i)),
    handledProps: ['state'],
  };
});

registerModsSerializer((option) => {
  const replayConditions = option.replayConditions ?? [];
  return {
    mods: replayConditions.flatMap((cond, i) => replayConditionToMods(cond, i)),
    handledProps: ['replay.state', 'replay.text', 'replay.goto', 'replay.icon'],
  };
});

/**
 * Serialize HubOption for PUT payload: run all registered serializers,
 * preserve mods for unhandled props, merge results.
 */
export function serializeHubOptionMods(option: HubOption): unknown[] {
  const existingMods = (option.mods ?? []) as Mod[];

  const allResults = modsSerializers.map((serialize) => serialize(option));
  const allHandledProps = new Set(allResults.flatMap((r) => r.handledProps));
  const allNewMods = allResults.flatMap((r) => r.mods);

  const preservedMods = existingMods.filter(
    (m) => !allHandledProps.has(m.prop),
  );

  return [...allNewMods, ...preservedMods];
}

/**
 * Deserialize mods with prop: "replay.*" into ReplayCondition[] for frontend UI.
 * Groups replay.state, replay.icon, replay.text, replay.goto by sortOrder —
 * same sortOrder = same condition card (mirrors TextDest pattern).
 */
export function deserializeReplayConditions(mods: unknown[]): ReplayCondition[] {
  const typedMods = (mods ?? []) as Mod[];
  const REPLAY_PROPS = ['replay.state', 'replay.icon', 'replay.text', 'replay.goto'] as const;

  const replayMods = typedMods.filter((m) => (REPLAY_PROPS as readonly string[]).includes(m.prop));

  const sortOrders = new Set(replayMods.map((m) => m.sortOrder));

  return [...sortOrders].sort((a, b) => a - b).map((so): ReplayCondition => {
    const group = replayMods.filter((m) => m.sortOrder === so);
    const stateMod = group.find((m) => m.prop === 'replay.state');
    const iconMod = group.find((m) => m.prop === 'replay.icon');
    const textMod = group.find((m) => m.prop === 'replay.text');
    const gotoMod = group.find((m) => m.prop === 'replay.goto');
    // Use whichever mod exists to reconstruct the when clause
    const sourceMod = stateMod ?? textMod ?? gotoMod ?? iconMod!;

    return {
      uuid: uuidV4(),
      quantifier: sourceMod.when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All,
      subConditions: sourceMod.when.rules.map((rule): SubCondition => ({
        uuid: uuidV4(),
        variableId: rule.variableId,
        operator: rule.operator as HubConditionOperator,
        ...fromRuleValue(rule.value),
      })),
      resultState: stateMod ? (stateMod.val.state as HubOptionStateValue) : null,
      resultIconId: iconMod ? (iconMod.val.iconId as number | null) : null,
      resultText: textMod ? (textMod.val.text as string) : null,
      resultGotoBranchId: gotoMod ? (gotoMod.val.gotoBranchId as number | null) : null,
    };
  });
}

// V1.2: title & goto serializer
registerModsSerializer((option) => {
  const textDestConditions = option.textDestConditions ?? [];
  return {
    mods: textDestConditions.flatMap((cond, i) => textDestConditionToMods(cond, i)),
    handledProps: ['title', 'goto'],
  };
});

/**
 * Deserialize mods with prop: "icon" into IconCondition[] for frontend UI.
 * 1:1 mapping — one mod = one condition (no grouping needed).
 */
export function deserializeIconConditions(mods: unknown[]): IconCondition[] {
  const typedMods = (mods ?? []) as Mod[];
  const iconMods = typedMods
    .filter((m) => m.prop === 'icon')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return iconMods.map((mod): IconCondition => ({
    uuid: uuidV4(),
    quantifier: mod.when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All,
    subConditions: mod.when.rules.map((rule): SubCondition => ({
      uuid: uuidV4(),
      variableId: rule.variableId,
      operator: rule.operator as HubConditionOperator,
      ...fromRuleValue(rule.value),
    })),
    resultIconId: (mod.val.iconId as number | null) ?? null,
  }));
}

// V1.3: icon serializer
registerModsSerializer((option) => {
  const iconConditions = option.iconConditions ?? [];
  return {
    mods: iconConditions.map((cond, i) => ({
      prop: 'icon',
      sortOrder: i,
      val: { iconId: cond.resultIconId },
      when: buildWhenClause(cond),
    })),
    handledProps: ['icon'],
  };
});

/**
 * Deserialize mods with prop "tags.top" or "tags.bottom" into TagCondition[].
 */
export function deserializeTagConditions(mods: unknown[], position: 'top' | 'bottom'): TagCondition[] {
  const typedMods = (mods ?? []) as Mod[];
  const prop = position === 'top' ? 'tags.top' : 'tags.bottom';
  const tagMods = typedMods
    .filter((m) => m.prop === prop)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return tagMods.map((mod): TagCondition => ({
    uuid: uuidV4(),
    quantifier: mod.when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All,
    subConditions: mod.when.rules.map((rule): SubCondition => ({
      uuid: uuidV4(),
      variableId: rule.variableId,
      operator: rule.operator as HubConditionOperator,
      ...fromRuleValue(rule.value),
    })),
    tagTemplateId: (mod.val.tagTemplateId as number) ?? 0,
  }));
}

registerModsSerializer((option) => {
  const tagTopConditions = option.tagTopConditions ?? [];
  return {
    mods: tagTopConditions.map((cond, i) => ({
      prop: 'tags.top',
      sortOrder: i,
      val: { tagTemplateId: cond.tagTemplateId },
      when: buildWhenClause(cond),
    })),
    handledProps: ['tags.top'],
  };
});

registerModsSerializer((option) => {
  const tagBottomConditions = option.tagBottomConditions ?? [];
  return {
    mods: tagBottomConditions.map((cond, i) => ({
      prop: 'tags.bottom',
      sortOrder: i,
      val: { tagTemplateId: cond.tagTemplateId },
      when: buildWhenClause(cond),
    })),
    handledProps: ['tags.bottom'],
  };
});

// Per-option transform registry for non-mod fields (e.g. score: stored as JSON column,
// not as a mod, so it bypasses the modsSerializers pipeline). Features self-register.
type HubOptionTransform = (option: HubOption) => void;

const hubOptionDeserializers: HubOptionTransform[] = [];
const hubOptionSerializers: HubOptionTransform[] = [];

export function registerHubOptionDeserializer(transform: HubOptionTransform): void {
  // Idempotent — protects against double-registration under HMR / re-imports.
  if (!hubOptionDeserializers.includes(transform)) hubOptionDeserializers.push(transform);
}

export function registerHubOptionSerializer(transform: HubOptionTransform): void {
  if (!hubOptionSerializers.includes(transform)) hubOptionSerializers.push(transform);
}

export function applyHubOptionDeserializers(option: HubOption): void {
  hubOptionDeserializers.forEach((transform) => transform(option));
}

export function applyHubOptionSerializers(option: HubOption): void {
  hubOptionSerializers.forEach((transform) => transform(option));
}

// HubSettings transform registry — step-level counterpart of the per-option registry above.
type HubSettingsTransform = (settings: HubSettings) => void;

const hubSettingsDeserializers: HubSettingsTransform[] = [];
const hubSettingsSerializers: HubSettingsTransform[] = [];

export function registerHubSettingsDeserializer(transform: HubSettingsTransform): void {
  if (!hubSettingsDeserializers.includes(transform)) hubSettingsDeserializers.push(transform);
}

export function registerHubSettingsSerializer(transform: HubSettingsTransform): void {
  if (!hubSettingsSerializers.includes(transform)) hubSettingsSerializers.push(transform);
}

export function applyHubSettingsDeserializers(settings: HubSettings): void {
  hubSettingsDeserializers.forEach((transform) => transform(settings));
}

export function applyHubSettingsSerializers(settings: HubSettings): void {
  hubSettingsSerializers.forEach((transform) => transform(settings));
}
